import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Button, Input, Label, Card, CardContent, CardHeader, CardTitle } from '../components/ui'
import { User, Mail, Phone, Shield, Calendar, Save, Edit3, AlertCircle, CheckCircle, Plus, Trash2, Star } from 'lucide-react'

interface ProfileData {
  id: string
  name?: string
  email?: string
  phone?: string
  role: string
  created_at: number
  last_login_at?: number
}

interface ContactMethod {
  id: string
  type: 'email' | 'phone'
  value: string
  is_primary: boolean
  is_verified: boolean
  provider?: string
  created_at: number
  verified_at?: number
}

export default function Profile() {
  const { } = useAuth()
  const [profileData, setProfileData] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: ''
  })
  const [contactMethods, setContactMethods] = useState<ContactMethod[]>([])
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [addingContact, setAddingContact] = useState<{ type: 'email' | 'phone' | null }>({ type: null })
  const [phoneForm, setPhoneForm] = useState({ phoneNumber: '', otp: '', step: 'phone' })
  const [verifyingPhone, setVerifyingPhone] = useState(false)

  // Load profile data
  useEffect(() => {
    fetchProfileData()
    fetchContactMethods()
  }, [])

  const fetchProfileData = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/auth/me`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to load profile data')
      }

      const data = await response.json()
      setProfileData(data.user)
      setFormData({
        name: data.user.name || ''
      })
    } catch (err) {
      console.error('Failed to fetch profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const fetchContactMethods = async () => {
    try {
      setLoadingContacts(true)
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/contact-methods`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        setContactMethods(data.contactMethods)
      }
    } catch (err) {
      console.error('Failed to fetch contact methods:', err)
    } finally {
      setLoadingContacts(false)
    }
  }

  const initiateEmailOAuth = async (provider: 'google' | 'microsoft') => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/contact-methods/oauth/initiate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ provider })
        }
      )

      if (response.ok) {
        const data = await response.json()
        window.location.href = data.authUrl
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to initiate OAuth')
      }
    } catch (err) {
      console.error('OAuth initiation error:', err)
      setError('Failed to start email verification')
    }
  }

  const initiatePhoneVerification = async () => {
    try {
      setVerifyingPhone(true)
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/contact-methods/phone/initiate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ phoneNumber: phoneForm.phoneNumber })
        }
      )

      if (response.ok) {
        setPhoneForm({ ...phoneForm, step: 'otp' })
        setSuccess('Verification code sent to your phone')
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to send verification code')
      }
    } catch (err) {
      console.error('Phone verification error:', err)
      setError('Failed to send verification code')
    } finally {
      setVerifyingPhone(false)
    }
  }

  const verifyPhone = async () => {
    try {
      setVerifyingPhone(true)
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/contact-methods/phone/verify`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            phoneNumber: phoneForm.phoneNumber,
            otp: phoneForm.otp 
          })
        }
      )

      if (response.ok) {
        const data = await response.json()
        if (data.merged) {
          setSuccess('User accounts merged successfully! Your phone number belonged to another account, and all data has been consolidated into your current account.')
        } else {
          setSuccess('Phone number added successfully!')
        }
        setAddingContact({ type: null })
        setPhoneForm({ phoneNumber: '', otp: '', step: 'phone' })
        fetchContactMethods()
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Invalid verification code')
      }
    } catch (err) {
      console.error('Phone verification error:', err)
      setError('Failed to verify phone number')
    } finally {
      setVerifyingPhone(false)
    }
  }

  const removeContactMethod = async (contactId: string) => {
    if (!confirm('Are you sure you want to remove this contact method?')) return

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/contact-methods/${contactId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (response.ok) {
        setSuccess('Contact method removed successfully')
        fetchContactMethods()
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to remove contact method')
      }
    } catch (err) {
      console.error('Remove contact method error:', err)
      setError('Failed to remove contact method')
    }
  }

  const setPrimaryContact = async (contactId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/contact-methods/${contactId}/primary`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (response.ok) {
        setSuccess('Primary contact method updated')
        fetchContactMethods()
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to update primary contact method')
      }
    } catch (err) {
      console.error('Set primary contact error:', err)
      setError('Failed to update primary contact method')
    }
  }

  // Handle OAuth callback results
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const success = urlParams.get('success')
    const error = urlParams.get('error')

    if (success === 'email_added') {
      setSuccess('Email address added successfully!')
      fetchContactMethods()
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (success === 'user_merged') {
      setSuccess('User accounts merged successfully! Your email belonged to another account, and all data has been consolidated into your current account.')
      fetchContactMethods()
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (error) {
      let errorMessage = 'Failed to add email address'
      switch (error) {
        case 'oauth_expired':
          errorMessage = 'OAuth session expired. Please try again.'
          break
        case 'email_already_yours':
          errorMessage = 'You already have this email address.'
          break
        case 'add_email_failed':
          errorMessage = 'Failed to add email address. Please try again.'
          break
      }
      setError(errorMessage)
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])


  const handleSave = async () => {
    if (!profileData) return

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/users/profile`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name.trim() || null
          })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to update profile')
      }

      const updatedData = await response.json()
      setProfileData(updatedData.user)
      setIsEditing(false)
      setSuccess('Profile updated successfully!')
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to update profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (profileData) {
      setFormData({
        name: profileData.name || ''
      })
    }
    setIsEditing(false)
    setError(null)
    setSuccess(null)
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatRole = (role: string) => {
    switch (role) {
      case 'OWNER':
        return 'Team Owner'
      case 'ADMIN':
        return 'Administrator'
      case 'MEMBER':
        return 'Team Member'
      default:
        return role
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="h-64 bg-gray-200 rounded-lg"></div>
            </div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!profileData) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Profile</h3>
          <p className="text-gray-600 mb-4">{error || 'Unable to load your profile information'}</p>
          <Button onClick={fetchProfileData}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account information and preferences</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-800">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Profile Information */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Personal Information
                </CardTitle>
                {!isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Name Field */}
              <div>
                <Label htmlFor="name">Display Name</Label>
                {isEditing ? (
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                    className="mt-1"
                  />
                ) : (
                  <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                    {profileData.name || (
                      <span className="text-gray-500 italic">No name set</span>
                    )}
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  This is how your name will appear to other team members
                </p>
              </div>

              {/* Email Field (Read-only) */}
              <div>
                <Label>Email Address</Label>
                <div className="mt-1 flex items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  {profileData.email || (
                    <span className="text-gray-500 italic">No email address</span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Email address cannot be changed from this page
                </p>
              </div>

              {/* Contact Methods Section */}
              <div>
                <Label>Contact Methods</Label>
                <div className="mt-2 space-y-3">
                  {loadingContacts ? (
                    <div className="text-sm text-gray-500">Loading contact methods...</div>
                  ) : contactMethods.length > 0 ? (
                    contactMethods.map((contact) => (
                      <div key={contact.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md">
                        <div className="flex items-center">
                          {contact.type === 'email' ? (
                            <Mail className="w-4 h-4 text-gray-400 mr-2" />
                          ) : (
                            <Phone className="w-4 h-4 text-gray-400 mr-2" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{contact.value}</span>
                              {contact.is_primary && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Primary</span>
                              )}
                              {contact.is_verified ? (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Verified</span>
                              ) : (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">Unverified</span>
                              )}
                            </div>
                            {contact.provider && (
                              <div className="text-xs text-gray-500 mt-1">Added via {contact.provider}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!contact.is_primary && contact.is_verified && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPrimaryContact(contact.id)}
                              title="Set as primary"
                            >
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeContactMethod(contact.id)}
                            className="text-red-600 hover:text-red-700"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 italic p-3 bg-gray-50 border border-gray-200 rounded-md">
                      No additional contact methods
                    </div>
                  )}
                  
                  {/* Add Contact Method Buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setAddingContact({ type: 'email' })}
                      disabled={addingContact.type !== null}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Email
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setAddingContact({ type: 'phone' })}
                      disabled={addingContact.type !== null}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Phone
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Manage your email addresses and phone numbers for authentication and notifications
                </p>
              </div>

              {/* Action Buttons */}
              {isEditing && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Add Contact Method Modal */}
        {addingContact.type && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">
                Add {addingContact.type === 'email' ? 'Email Address' : 'Phone Number'}
              </h3>
              
              {addingContact.type === 'email' ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Choose a provider to add your email address:
                  </p>
                  <div className="space-y-2">
                    <Button
                      className="w-full"
                      onClick={() => initiateEmailOAuth('google')}
                    >
                      Add Google Email
                    </Button>
                    <Button
                      className="w-full"
                      onClick={() => initiateEmailOAuth('microsoft')}
                    >
                      Add Microsoft Email
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {phoneForm.step === 'phone' ? (
                    <>
                      <div>
                        <Label htmlFor="phoneNumber">Phone Number</Label>
                        <Input
                          id="phoneNumber"
                          type="tel"
                          value={phoneForm.phoneNumber}
                          onChange={(e) => setPhoneForm({ ...phoneForm, phoneNumber: e.target.value })}
                          placeholder="+1 (555) 123-4567"
                          className="mt-1"
                        />
                      </div>
                      <Button
                        className="w-full"
                        onClick={initiatePhoneVerification}
                        disabled={!phoneForm.phoneNumber || verifyingPhone}
                      >
                        {verifyingPhone ? 'Sending...' : 'Send Verification Code'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div>
                        <Label htmlFor="otp">Verification Code</Label>
                        <Input
                          id="otp"
                          type="text"
                          value={phoneForm.otp}
                          onChange={(e) => setPhoneForm({ ...phoneForm, otp: e.target.value })}
                          placeholder="123456"
                          className="mt-1"
                          maxLength={6}
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Enter the 6-digit code sent to {phoneForm.phoneNumber}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          className="flex-1"
                          onClick={verifyPhone}
                          disabled={phoneForm.otp.length !== 6 || verifyingPhone}
                        >
                          {verifyingPhone ? 'Verifying...' : 'Verify'}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => setPhoneForm({ ...phoneForm, step: 'phone', otp: '' })}
                          disabled={verifyingPhone}
                        >
                          Back
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setAddingContact({ type: null })
                    setPhoneForm({ phoneNumber: '', otp: '', step: 'phone' })
                    setError(null)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Account Information Sidebar */}
        <div className="space-y-6">
          {/* Account Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Account Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-700">User ID</Label>
                <p className="text-sm text-gray-600 font-mono bg-gray-50 px-2 py-1 rounded mt-1">
                  {profileData.id}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700">Role</Label>
                <div className="flex items-center mt-1">
                  <Shield className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">{formatRole(profileData.role)}</span>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700">Member Since</Label>
                <div className="flex items-center mt-1">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">
                    {formatDate(profileData.created_at)}
                  </span>
                </div>
              </div>

              {profileData.last_login_at && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Last Login</Label>
                  <div className="flex items-center mt-1">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {formatDate(profileData.last_login_at)}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Account Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="secondary"
                className="w-full justify-start"
                onClick={() => {
                  // TODO: Implement change password/auth method
                  alert('Change authentication method coming soon!')
                }}
              >
                <Shield className="w-4 h-4 mr-2" />
                Security Settings
              </Button>
              
              <Button
                variant="secondary"
                className="w-full justify-start"
                onClick={() => {
                  // TODO: Implement export data
                  alert('Export data coming soon!')
                }}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Export My Data
              </Button>

              <div className="pt-3 border-t">
                <Button
                  variant="danger"
                  className="w-full justify-start"
                  onClick={() => {
                    // TODO: Implement account deletion
                    if (confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
                      alert('Account deletion coming soon!')
                    }
                  }}
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}